import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { GlassCard } from '@/components/GlassCard';
import { FollowButton } from '@/components/FollowButton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Radio, CalendarDays } from 'lucide-react';
import { timeAgo } from '@/lib/utils';
import { FullPageLoader } from '@/components/Loaders';
import { Seo } from '@/components/Seo';
import { FollowersDialog } from '@/components/FollowersDialog';

export default function UserProfilePage() {
  const { userId } = useParams();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [followersOpen, setFollowersOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/users/${userId}/public`),
      api.get(`/posts?user_id=${userId}&limit=30`),
    ])
      .then(([p, po]) => { setProfile(p.data); setPosts(po.data || []); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <FullPageLoader />;
  if (error || !profile) {
    return (
      <div className="mx-auto max-w-md px-5 py-16 text-center text-white">
        <p className="text-white/70">This profile doesn't exist.</p>
        <Button onClick={() => nav(-1)} variant="outline" className="mt-4 rounded-full border-white/15 bg-white/5 text-white hover:bg-white/10">Go back</Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-4 pb-28 pt-4" data-testid="user-profile-page">
      <Seo title={`${profile.display_name} | Thirrsty.com`} path={`/user/${userId}`} noindex />
      <button onClick={() => nav(-1)} className="mb-3 flex items-center gap-1.5 text-sm text-white/70 hover:text-white" data-testid="user-profile-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <GlassCard className="p-5">
        <div className="flex items-center gap-4">
          <div className="relative">
            <Avatar className="h-16 w-16 border-2 border-[#F6B24A]/50">
              <AvatarImage src={profile.avatar_url} />
              <AvatarFallback className="bg-[#F6B24A]/25 text-lg text-[#F6B24A]">{(profile.display_name || 'U').slice(0, 1)}</AvatarFallback>
            </Avatar>
            {profile.live_room && (
              <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 rounded-full bg-red-600 px-1.5 py-0.5 text-[9px] font-bold text-white">LIVE</span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="truncate font-display text-xl font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }} data-testid="user-profile-name">
              {profile.display_name}
            </h1>
            <div className="mt-1 flex items-center gap-3 text-xs text-white/60">
              <button onClick={() => setFollowersOpen(true)} className="transition-colors hover:text-[#F6B24A]" data-testid="user-profile-followers"><b className="text-white">{profile.followers}</b> followers</button>
              <span><b className="text-white">{profile.posts_count}</b> posts</span>
            </div>
            {profile.created_at && (
              <p className="mt-1 flex items-center gap-1 text-[11px] text-white/45">
                <CalendarDays className="h-3 w-3" /> Joined {timeAgo(profile.created_at)} ago
              </p>
            )}
          </div>
        </div>
        {profile.bio && (
          <p className="mt-3 text-sm leading-relaxed text-white/75" data-testid="user-profile-bio">{profile.bio}</p>
        )}
        <FollowersDialog userId={userId} open={followersOpen} onOpenChange={setFollowersOpen} />
        <div className="mt-4">
          {profile.is_me ? (
            <Button asChild variant="outline" size="sm" className="rounded-full border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid="user-profile-edit-own">
              <Link to="/profile">This is you — edit profile</Link>
            </Button>
          ) : (
            <FollowButton targetType="user" targetId={profile.id} />
          )}
        </div>
      </GlassCard>

      <h2 className="mb-2 mt-6 text-xs uppercase tracking-[0.18em] text-white/60">Posts</h2>
      {posts.length === 0 ? (
        <p className="text-sm text-white/50">No posts yet.</p>
      ) : (
        <div className="space-y-3">
          {posts.map((post) => (
            <GlassCard key={post.id} className="overflow-hidden" data-testid={`user-profile-post-${post.id}`}>
              {post.image_url && <img src={post.image_url} alt="" className="max-h-80 w-full object-cover" />}
              {post.video_url && <video src={post.video_url} controls playsInline className="max-h-80 w-full bg-black" />}
              <div className="p-3">
                {post.text && <p className="text-sm text-white/90">{post.text}</p>}
                <div className="mt-1 flex items-center gap-2 text-[11px] text-white/50">
                  {post.venue_name && (
                    <span className="flex items-center gap-1 text-[#F6B24A]"><Radio className="h-3 w-3" /> {post.venue_name}</span>
                  )}
                  <span>{timeAgo(post.created_at)} ago</span>
                </div>
              </div>
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  );
}
